import { useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from '../../hooks/useTranslation';
import { encUrl } from '../../utils/encUrl';
import { getCover, photoPath } from '../../utils/collections';
import EncryptedImage from '../../components/EncryptedImage/EncryptedImage';
import styles from './Home.module.css';

// ─── Stories video config ────────────────────────────────────────────────────
// Drop your encoded files in /public/videos/stories/ and update the paths below.
// Poster images show until real videos are available (placeholder mode).
const STORY_VIDEOS = [
  { webm: '/videos/stories/story-1.webm', mp4: '/videos/stories/story-1.mp4' },
  { webm: '/videos/stories/story-2.webm', mp4: '/videos/stories/story-2.mp4' },
  { webm: '/videos/stories/story-3.webm', mp4: '/videos/stories/story-3.mp4' },
];

// Collection covers are chosen in Photo Studio (collections.json `cover`)
const coverSrc = (slug) => {
  const photo = getCover(slug);
  return photo ? encUrl(photoPath(photo)) : undefined;
};

const Home = () => {
  const { t } = useTranslation();

  const bannerRef  = useRef(null);
  const videoRefs  = useRef([]);

  // Lazy-load + autoplay videos only when the banner scrolls into view.
  // Mobile is skipped intentionally — poster images serve as static backgrounds.
  useEffect(() => {
    const banner = bannerRef.current;
    if (!banner) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;

          const isMobile = window.innerWidth <= 768;
          videoRefs.current.forEach((video, i) => {
            if (!video) return;
            if (isMobile && i !== 2) return; // mobile: story-3 only
            video.load();
            video.play().catch(() => {});
          });

          observer.disconnect();
        });
      },
      { threshold: 0.1 }
    );

    observer.observe(banner);
    return () => observer.disconnect();
  }, []);

  return (
    <div className={styles.home}>

      {/* ── Hero ── */}
      {/* Optimized + encrypted like the portfolio: build/images (originals) is never deployed */}
      <section className={styles.hero}>
        <EncryptedImage
          src={encUrl('/images-optimized/background/background-9.jpg')}
          alt=""
          className={styles.heroImage}
          cover
        />
        <div className={styles.heroOverlay}>
          <div className={styles.heroContent}>
            <h1 className={styles.heroTitle}>{t('home.heroTitle')}</h1>
            <p className={styles.heroSubtitle}>{t('home.heroSubtitle')}</p>
          </div>
          <div className={styles.scrollIndicator}>
            <span className={styles.scrollArrow}></span>
          </div>
        </div>
      </section>

      {/* ── Stories banderolle ── */}
      <Link to="/stories" className={styles.storiesBanner}>

        {/* 3-column video grid */}
        <div className={styles.storiesVideoGrid} ref={bannerRef}>
          {STORY_VIDEOS.map((clip, i) => (
            <div key={i} className={styles.storiesVideoCell}>
              <video
                ref={(el) => { videoRefs.current[i] = el; }}
                className={styles.storiesVideo}
                muted
                loop
                playsInline
                preload="none"
                poster={clip.poster}
              >
                <source src={clip.mp4}  type="video/mp4"  />
                <source src={clip.webm} type="video/webm" />
              </video>
            </div>
          ))}
        </div>

        {/* Text overlay */}
        <div className={styles.storiesBannerOverlay}>
          <div className={styles.storiesBannerContent}>
            <span className={styles.storiesBannerLabel}>{t('home.storiesBannerLabel')}</span>
            <h2 className={styles.storiesBannerTitle}>{t('home.storiesBannerTitle')}</h2>
            <p className={styles.storiesBannerSubtitle}>{t('home.storiesBannerSubtitle')}</p>
            <span className={styles.storiesBannerBadge}>{t('home.storiesBannerBadge')}</span>
          </div>
        </div>

      </Link>

      {/* ── Collections ── */}
      <section className={styles.categories}>
        <h2 className={styles.collectionsTitle}>{t('home.collectionsTitle')}</h2>
        <div className={styles.collectionsGrid}>

          <Link to="/portfolio/wildlife" className={`${styles.cell} ${styles.cellFeatured}`}>
            <EncryptedImage
              src={coverSrc('wildlife')}
              alt={t('home.categories.wildlife')}
              className={styles.cellImage}
              cover
            />
            <div className={styles.cellOverlay} />
            <span className={styles.cellTitle}>{t('home.categories.wildlife')}</span>
          </Link>

          <Link to="/portfolio/landscape" className={styles.cell}>
            <EncryptedImage
              src={coverSrc('landscape')}
              alt={t('home.categories.landscape')}
              className={styles.cellImage}
              cover
            />
            <div className={styles.cellOverlay} />
            <span className={styles.cellTitle}>{t('home.categories.landscape')}</span>
          </Link>

          <Link to="/portfolio/travels" className={styles.cell}>
            <EncryptedImage
              src={coverSrc('travels')}
              alt={t('home.categories.travels')}
              className={styles.cellImage}
              cover
            />
            <div className={styles.cellOverlay} />
            <span className={styles.cellTitle}>{t('home.categories.travels')}</span>
          </Link>

        </div>
      </section>

      {/* ── CTA ── */}
      <section className={styles.cta}>
        <Link to="/about" className={styles.ctaButton}>
          {t('home.cta')}
        </Link>
      </section>

    </div>
  );
};

export default Home;
